import { constantCase } from "@portal/app/_common/lib/changeCase";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import {
  ELIGIBILITY_REQUIREMENT,
  PROGRAM_ENVIRONMENT,
  PROGRAM_LEVEL,
  PROGRAM_TAG,
  EligibilityRequirement,
  ProgramEnvironment,
  ProgramLevel,
  ProgramTag,
} from "@portal/app/_common/types/program";
import { VOCATION_INDUSTRY, VocationIndustry } from "@portal/app/_common/types/vocationIndustry";
import { US_STATE, USState } from "@portal/app/_common/types/usState";
import { PROVIDER_TYPE, ProviderType } from "@portal/app/_common/types/provider";
import {
  SCHOOL_PAYMENT_OPTION_TYPES,
  SchoolPaymentOptionType,
} from "@portal/app/_common/types/paymentOptions";
import {
  VOCATION_SUBINDUSTRY,
  VocationSubindustry,
} from "@portal/app/_common/types/vocationSubindustry";

// Helpers for assertion and typecasting to DreamSearch filter enums
export const assertProgramEnvironment = (v: string | undefined) =>
  v && PROGRAM_ENVIRONMENT.includes(constantCase(v) as ProgramEnvironment)
    ? (constantCase(v) as ProgramEnvironment)
    : undefined;

export const assertProgramEligibilityRequirement = (v: string | undefined) =>
  v && Object.values(ELIGIBILITY_REQUIREMENT).includes(constantCase(v) as EligibilityRequirement)
    ? (constantCase(v) as EligibilityRequirement)
    : undefined;

export const assertIndustry = (v: string | undefined) =>
  v && Object.values(VOCATION_INDUSTRY).includes(constantCase(v) as VocationIndustry)
    ? (constantCase(v) as VocationIndustry)
    : undefined;

export const assertSubindustry = (v: string | undefined) =>
  v && Object.values(VOCATION_SUBINDUSTRY).includes(constantCase(v) as VocationSubindustry)
    ? (constantCase(v) as VocationSubindustry)
    : undefined;

export const assertSchoolState = (v: string | undefined) =>
  v && Object.values(US_STATE).includes(constantCase(v) as USState)
    ? (constantCase(v) as USState)
    : undefined;

export const assertSchoolType = (v: string | undefined) =>
  v && Object.values(PROVIDER_TYPE).includes(constantCase(v) as ProviderType)
    ? (constantCase(v) as ProviderType)
    : undefined;

export const assertPaymentOptionType = (v: string | undefined) =>
  v &&
  Object.values(SCHOOL_PAYMENT_OPTION_TYPES).includes(constantCase(v) as SchoolPaymentOptionType)
    ? (constantCase(v) as SchoolPaymentOptionType)
    : undefined;

export const assertProgramTag = (v: string | undefined) =>
  v && Object.values(PROGRAM_TAG).includes(constantCase(v) as ProgramTag)
    ? (constantCase(v) as ProgramTag)
    : undefined;

export const assertProgramLevel = (v: string | undefined) =>
  v && Object.values(PROGRAM_LEVEL).includes(constantCase(v) as ProgramLevel)
    ? (constantCase(v) as ProgramLevel)
    : undefined;

// Helpers for casting from query params to DreamSearch filter primitives
export const cleanURIComponentString = <Input extends string | string[] | undefined>(
  queryInput: Input
): Input => {
  if (typeof queryInput === "string") {
    return decodeURIComponent(queryInput)?.match(/[a-zA-Z0-9-_]+/g)?.[0] as Input;
  } else {
    return (
      typeof queryInput === "object" ? queryInput.map(cleanURIComponentString) : undefined
    ) as Input;
  }
};

// Matches a delimiting comma in either the form it can reach us in. Traffic
// arriving through ad-platform redirects is frequently encoded more than once, and
// `cleanURIComponentString` decodes a second time for exactly that reason — so by
// the time a value gets here the separator may still be sitting as `%2C`. Splitting
// on both catches it without decoding first, which would double-decode the segments
// and make an ordinary literal like `100%25` throw.
const COMMA_DELIMITER = /,|%2c/i;

// Ad platforms (notably Facebook) collapse repeated query-param keys, so a
// multi-value filter can only reach us comma-delimited — `?school_name=technical,american`
// instead of `?school_name=technical&school_name=american`. Split those apart and
// sanitize each value on its own, rather than letting the first one swallow the rest.
//
// Segments that sanitize away to nothing (`technical,,american`, a stray trailing
// comma) are dropped rather than left as holes: the single-value params read
// positionally off index 0, so a leading hole would blank them out entirely. A value
// that yields one segment keeps its original string shape for the same reason, which
// makes this a no-op for every param that isn't comma-delimited today.
export const cleanQueryParamValue = (
  queryInput: string | string[] | undefined
): string | string[] | undefined => {
  if (queryInput === undefined) {
    return undefined;
  }

  const values = (Array.isArray(queryInput) ? queryInput : [queryInput])
    .flatMap((value) => value.split(COMMA_DELIMITER))
    .map((segment) => cleanURIComponentString(segment))
    .filter((segment): segment is string => segment !== undefined);

  return Array.isArray(queryInput) || values.length > 1 ? values : values[0];
};

export const getFilterStringArray = (input: string | string[] | undefined): string[] | undefined =>
  [...(Array.isArray(input) ? input : [input])].filter((v): v is string => v !== undefined);

export const getFilterEnumArray = <T>(
  input: string | string[] | undefined,
  convertFunction: (v: string | undefined) => T | undefined
): T[] =>
  [...(Array.isArray(input) ? input.map(convertFunction) : [convertFunction(input)])].filter(
    (v): v is T => v !== undefined
  );

export const getFilterString = (input: string | string[] | undefined) =>
  (Array.isArray(input) ? input : [input])[0];

export const getFilterNumber = (input: string | string[] | undefined) => {
  if (!input) {
    return undefined;
  }

  return Array.isArray(input) ? parseInt(input[0]) : parseInt(input);
};

export const getFilterBoolean = (input: string | string[] | undefined) => {
  if (!input) {
    return undefined;
  }

  return Array.isArray(input) ? input[0] === "true" : input === "true";
};

// Helpers for casting from DreamSearch filters to query params
export const getParamArray = (input: string[] | undefined): string | string[] | undefined => {
  if (input) {
    if (input.length === 0) {
      return undefined;
    }
    if (input.length === 1) {
      return kebabCase(input[0]);
    }
    return input.map((t) => kebabCase(t));
  }

  return undefined;
};

export const getParamStringFromBoolean = (input: boolean | undefined): string | undefined =>
  input ? "true" : input === false ? "false" : undefined;

export const getParamString = (input: string | undefined): string | undefined =>
  input ? kebabCase(input) : undefined;

export const getParamStringFromNumber = (input: number | undefined): string | undefined =>
  input ? kebabCase(input.toString()) : undefined;
